import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { SectionHeader } from "@/components/Brand";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function SurveyForm() {
  const { wardId, boothId } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [ward, setWard] = useState(null);
  const [booth, setBooth] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [location, setLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/form-template").then((r) => setTpl(r.data));
    api.get("/surveyor/wards").then((r) => setWard(r.data.find((w) => w.id === wardId)));
    api.get(`/wards/${wardId}/booths`).then((r) => setBooth(r.data.find((b) => b.id === boothId)));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 5000 },
      );
    }
  }, [wardId, boothId]);

  // Prefill booth identification
  useEffect(() => {
    if (ward && booth && !answers.q1_1) {
      setAnswers((a) => ({
        ...a,
        q1_1: ward.name,
        q1_2: ward.number,
        q1_3: booth.booth_number,
        q1_4: booth.polling_station,
        q1_5: booth.area,
        q1_7: new Date().toISOString().split("T")[0],
      }));
    }
  }, [ward, booth]);

  const totalSteps = tpl?.sections.length || 0;
  const current = tpl?.sections[step];

  const setAns = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const validateStep = () => {
    if (!current) return true;
    for (const q of current.questions) {
      if (!q.required) continue;
      const v = answers[q.id];
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
        toast.error(`Required: ${q.label}`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await api.post("/surveys", { ward_id: wardId, booth_id: boothId, answers, location });
      setDone(true);
      toast.success("Survey submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!tpl || !current)
    return <div className="text-sm text-slate-500">Loading form…</div>;

  if (done) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="mt-4 text-2xl font-semibold text-slate-900">Submission saved</h3>
        <p className="text-sm text-slate-600 mt-2">{ward?.name} · Booth {booth?.booth_number}</p>
        <div className="mt-6 flex justify-center gap-2">
          <Link
            to={`/surveyor/wards/${wardId}`}
            className="px-4 py-2 bg-blue-700 text-white rounded-md text-sm font-medium"
            data-testid="survey-next-booth"
          >
            Submit another
          </Link>
          <Link
            to="/surveyor"
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-md text-sm font-medium"
          >
            Back to wards
          </Link>
        </div>
      </div>
    );
  }

  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <div>
      <Link to={`/surveyor/wards/${wardId}`} className="inline-flex items-center gap-1 text-sm text-slate-600 mb-3 hover:text-slate-900">
        <ArrowLeft size={14} /> Back to booths
      </Link>

      <div className="mb-4">
        <div className="flex justify-between text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">
          <span>Section {step + 1} of {totalSteps}</span>
          {location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={11} /> {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
            </span>
          )}
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-700 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <SectionHeader title={current.title} />

      <div className="space-y-5 wi-card p-5">
        {current.questions.map((q) => (
          <QuestionInput
            key={q.id}
            q={q}
            value={answers[q.id]}
            onChange={(v) => setAns(q.id, v)}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          data-testid="survey-prev"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-200 text-sm disabled:opacity-50"
        >
          <ArrowLeft size={14} /> Previous
        </button>
        {step < totalSteps - 1 ? (
          <button
            onClick={() => { if (validateStep()) setStep((s) => s + 1); }}
            data-testid="survey-next"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium"
          >
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="survey-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium"
          >
            <CheckCircle2 size={14} /> {submitting ? "Submitting…" : "Submit survey"}
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  const label = (
    <span className="block text-sm font-medium text-slate-800 mb-1.5">
      {q.label} {q.required && <span className="text-red-500">*</span>}
    </span>
  );
  const cls = "w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent";

  switch (q.type) {
    case "short_text":
      return <label className="block">{label}<input data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></label>;
    case "long_text":
      return <label className="block">{label}<textarea rows={3} data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></label>;
    case "number":
      return <label className="block">{label}<input type="number" data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></label>;
    case "date":
      return <label className="block">{label}<input type="date" data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></label>;
    case "rating": {
      const v = Number(value || 0);
      return (
        <div>{label}<div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => onChange(n)} data-testid={`q-${q.id}-${n}`}
            className={`w-10 h-10 rounded-md border text-sm font-medium ${v === n ? "bg-blue-700 text-white border-blue-700" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
            {n}
          </button>
        ))}</div></div>
      );
    }
    case "multiple_choice":
      return (
        <div>{label}<div className="flex flex-wrap gap-2">{(q.options || []).map((opt) => (
          <button key={opt} type="button" onClick={() => onChange(opt)} data-testid={`q-${q.id}-${opt}`}
            className={`px-3 py-1.5 text-sm rounded-md border ${value === opt ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-300 hover:border-blue-400"}`}>
            {opt}
          </button>
        ))}</div></div>
      );
    case "dropdown":
      return <label className="block">{label}<select data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}><option value="">Select…</option>{(q.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></label>;
    case "checkboxes": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
      return (
        <div>{label}<div className="flex flex-wrap gap-2">{(q.options || []).map((opt) => {
          const on = arr.includes(opt);
          return (
            <button type="button" key={opt} onClick={() => toggle(opt)} data-testid={`q-${q.id}-${opt}`}
              className={`px-3 py-1.5 text-sm rounded-md border ${on ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-300 hover:border-blue-400"}`}>
              {opt}
            </button>
          );
        })}</div></div>
      );
    }
    case "file":
      return <FileInput q={q} value={value} onChange={onChange} label={label} />;
    default:
      return <label className="block">{label}<input data-testid={`q-${q.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></label>;
  }
}

function FileInput({ q, value, onChange, label }) {
  const list = Array.isArray(value) ? value : [];
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const newOnes = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await fetch(`${API_BASE}/uploads`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        });
        if (r.ok) {
          const data = await r.json();
          newOnes.push({ id: data.id, name: data.original_filename });
        }
      } catch {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    onChange([...list, ...newOnes]);
    setBusy(false);
  };
  return (
    <div>{label}
      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer text-sm text-slate-600 hover:bg-slate-50">
        <Upload size={14} /> {busy ? "Uploading…" : "Tap to add photo(s)"}
        <input type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid={`q-${q.id}`} />
      </label>
      {list.length > 0 && (
        <div className="mt-2 space-y-1">
          {list.map((f, i) => (
            <div key={f.id} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1 rounded">
              <span className="truncate">{f.name}</span>
              <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
